import { ActivityType, ChoiceType } from 'interfaces/common.interface';
import {
  HABIT_CHOICE_TYPE,
  LOG_SUMMARY_OPTION,
  MAXIMUM_ALLOWED_MINUTES
} from 'constants/general';
import { convertToSeconds } from 'utils/helpers';
import { areLogQuantityQuestionsValid } from 'utils/log-quantity-questions.util';
import { isVimeoURL, isYoutubeURL } from 'utils/validators';

const VALID_CHOICE_TYPES = new Set<string>([
  HABIT_CHOICE_TYPE.NONE,
  HABIT_CHOICE_TYPE.RANDOM,
  HABIT_CHOICE_TYPE.CHECK_LIST
]);

const VALID_LOG_SUMMARY_TYPES = new Set<string>([
  LOG_SUMMARY_OPTION.SUMMATION,
  LOG_SUMMARY_OPTION.AVERAGE
]);

const getTrimmedVideoUrls = (videoUrls: string[]): string[] =>
  videoUrls.map((url) => url.trim());

const hasUniqueVideoUrls = (videoUrls: string[]): boolean => {
  const trimmedUrls = getTrimmedVideoUrls(videoUrls);
  return new Set(trimmedUrls).size === trimmedUrls.length;
};

export const isValidActivityChoiceVideoUrl = (url: string): boolean => {
  const trimmedUrl = url.trim();
  return (
    Boolean(trimmedUrl) && (isYoutubeURL(trimmedUrl) || isVimeoURL(trimmedUrl))
  );
};

const areChoiceVideoUrlsValid = (videoUrls: string[] = []): boolean =>
  hasUniqueVideoUrls(videoUrls) &&
  videoUrls.every(isValidActivityChoiceVideoUrl);

const areChoiceLogQuestionsValid = (choice: ChoiceType): boolean =>
  areLogQuantityQuestionsValid(
    choice.log_quantity_questions,
    choice.log_quantity
  );

const isChoiceLogSummaryTypeValid = (choice: ChoiceType): boolean => {
  if (!choice.log_quantity) return true;
  return VALID_LOG_SUMMARY_TYPES.has(
    choice.log_summary_type ?? LOG_SUMMARY_OPTION.SUMMATION
  );
};

const isChoiceDurationValid = (choice: ChoiceType): boolean => {
  const duration = Number(choice.duration_seconds ?? 0);
  return duration > 0 && duration <= convertToSeconds(MAXIMUM_ALLOWED_MINUTES);
};

export const isActivityRandomChoiceValid = (choice: ChoiceType): boolean =>
  Boolean(choice.name?.trim()) &&
  isChoiceDurationValid(choice) &&
  isChoiceLogSummaryTypeValid(choice) &&
  areChoiceLogQuestionsValid(choice) &&
  areChoiceVideoUrlsValid(choice.video_urls);

const areRandomChoicesValid = (choices: ChoiceType[] = []): boolean =>
  choices.length > 0 && choices.every(isActivityRandomChoiceValid);

const isChecklistValid = (checkList: string[] = []): boolean =>
  checkList.length > 0 && checkList.every((item) => Boolean(item.trim()));

export const isActivityChoicesDataValid = (
  activity?: ActivityType
): boolean => {
  if (!activity) return false;

  const choiceType = activity.choice_type || HABIT_CHOICE_TYPE.NONE;
  if (!VALID_CHOICE_TYPES.has(choiceType)) return false;
  if (choiceType === HABIT_CHOICE_TYPE.NONE) return true;
  if (choiceType === HABIT_CHOICE_TYPE.CHECK_LIST) {
    return isChecklistValid(activity.check_list);
  }

  return areRandomChoicesValid(activity.choices);
};
